import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { loadCenters, regist } from '../../lib/api/clubs';
import palette from '../../lib/styles/palette';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  min-width: 440px;
  max-width: 520px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(0, 0, 0, 0.12);
  overflow: visible;
`;

const Title = styled.h3`
  margin: 0;
  padding: 18px 24px;
  background: #f8f9fb;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e0e4ea;
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
`;

const Body = styled.div`
  padding: 24px;
`;

const FormRow = styled.div`
  margin-bottom: 18px;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #3a4656;
  }

  input, select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${palette.inputBorder};
    border-radius: 6px;
    font-size: 0.95rem;
    color: #2c3e50;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  input::placeholder {
    color: #868e96;
  }

  input:focus {
    outline: none;
    border-color: ${palette.inputBorderFocus};
    box-shadow: 0 0 0 3px rgba(39, 94, 254, 0.1);
  }
`;

const BtnGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e0e4ea;
`;

const BtnPrimary = styled.button`
  padding: 10px 20px;
  background: ${palette.webCyan[5]};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BtnCancel = styled.button`
  padding: 10px 20px;
  background: #fff;
  color: ${palette.webCyan[5]};
  border: 1px solid ${palette.webCyan[5]};
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${palette.webCyan[5]};
    color: #fff;
  }
`;

const ClubCreateModal = ({ open, onClose, socketKey, user, onSuccess }) => {
  const [centerList, setCenterList] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [formData, setFormData] = useState({
    clubName: '',
    phone: '',
    address: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingCenters, setLoadingCenters] = useState(false);

  useEffect(() => {
    if (open && socketKey) {
      fetchCenters();
    }
  }, [open, socketKey]);

  const fetchCenters = async () => {
    setLoadingCenters(true);
    try {
      const res = await loadCenters({ socketKey });
      if (res?.data && Array.isArray(res.data)) {
        setCenterList(res.data.map((c) => ({
          label: `${c.name} (ID: ${c.centerID}), OwnerID: ${c.ownerID}`,
          ownerID: c.ownerID,
          value: c.centerID,
        })));
      }
    } catch (e) {
      console.error('센터 목록 로드 실패:', e);
      setCenterList([]);
    } finally {
      setLoadingCenters(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!socketKey || !user?.playerID) {
      alert('로그인이 필요합니다.');
      return;
    }

    const { clubName, phone, address } = formData;
    if (!clubName || !phone) {
      alert('클럽이름, 전화번호를 입력해주세요.');
      return;
    }

    if (!selectedCenter?.value) {
      alert('센터를 선택해주세요.');
      return;
    }
    const centerID = selectedCenter.value;
    const ownerID = selectedCenter.ownerID;

    setLoading(true);
    try {
      const res = await regist({
        centerID,
        name: clubName,
        ownerID: Number(ownerID),
        phone,
        address: address || '',
        socketKey,
      });

      if (res?.data?.result === 1 || res?.data?.clubInfo) {
        alert('클럽이 생성되었습니다.');
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        alert(res?.data?.result || '클럽 생성에 실패했습니다.');
      }
    } catch (err) {
      console.error(err);
      alert('클럽 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      clubName: '',
      phone: '',
      address: '',
    });
    setSelectedCenter(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Title>클럽 추가</Title>
        <Body>
        <form onSubmit={handleSubmit}>
          <FormRow>
            <label>센터</label>
            <Select
              options={centerList}
              value={selectedCenter}
              onChange={setSelectedCenter}
              placeholder={loadingCenters ? '로딩 중...' : '센터 선택'}
              isDisabled={loadingCenters}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: '100%',
                }),
              }}
            />
          </FormRow>
          <FormRow>
            <label>클럽 이름</label>
            <input
              type="text"
              name="clubName"
              value={formData.clubName}
              onChange={handleChange}
              placeholder="클럽 이름"
              required
            />
          </FormRow>
          <FormRow>
            <label>전화번호</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="전화번호"
              required
            />
          </FormRow>
          <FormRow>
            <label>주소 (선택)</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="주소"
            />
          </FormRow>
          <BtnGroup>
            <BtnCancel type="button" onClick={handleClose}>
              취소
            </BtnCancel>
            <BtnPrimary type="submit" disabled={loading}>
              {loading ? '처리 중...' : '생성'}
            </BtnPrimary>
          </BtnGroup>
        </form>
        </Body>
      </ModalBox>
    </Overlay>
  );
};

export default ClubCreateModal;
